/**
 * MinistrareIA - Chat Utilities (Simplified)
 * Simple formatting utilities for chat display
 * Business logic (deduplication, normalization) is handled by backend
 * 
 * UPDATED v2:
 * - Fixed: "Você" appearing as chat name when lastMessage is fromMe
 */

/**
 * Check if JID is a group
 */
export function isGroup(jid) {
  return jid?.includes('@g.us') || false;
}

/**
 * Get display name for a chat
 * Priority: pushName > name > lastMessage.pushName (only if NOT fromMe) > formatted phone
 * 
 * Note: Evolution API often returns chats without pushName,
 * but the pushName comes in the messages via webhook/SSE.
 * So we also check lastMessage.pushName as fallback.
 * 
 * IMPORTANT: We should NOT use lastMessage.pushName when the message is fromMe,
 * because it would show "Você" as the chat name.
 */
export function getChatDisplayName(chat) {
  // Direct chat properties
  if (chat.pushName) return chat.pushName;
  if (chat.name) return chat.name;
  
  // Try to get from lastMessage (comes from SSE/webhook)
  // BUT only if it's NOT from me (to avoid showing "Você" as name)
  const lastMsg = chat.lastMessage;
  const isFromMe = lastMsg?.key?.fromMe === true;
  const lastMsgPushName = lastMsg?.pushName;
  
  // Only use lastMessage.pushName if:
  // 1. It exists
  // 2. The message is NOT from me
  // 3. It's not "Você" (double safety)
  if (lastMsgPushName && !isFromMe && lastMsgPushName !== 'Você') {
    return lastMsgPushName;
  }
  
  // Fallback to formatted phone number
  const phone = chat._phoneNumber || chat.remoteJid?.split('@')[0];
  return phone ? formatPhoneNumber(phone) : 'Desconhecido';
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone) {
  if (!phone) return '';
  
  const clean = phone.replace(/\D/g, '');
  
  // Brazilian format: +55 (11) 99999-9999
  if (clean.length === 13 && clean.startsWith('55')) {
    const ddd = clean.slice(2, 4);
    const part1 = clean.slice(4, 9);
    const part2 = clean.slice(9);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }
  
  if (clean.length === 12 && clean.startsWith('55')) {
    const ddd = clean.slice(2, 4);
    const part1 = clean.slice(4, 8);
    const part2 = clean.slice(8);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }
  
  // International format
  if (clean.length > 10) {
    return `+${clean.slice(0, 2)} ${clean.slice(2)}`;
  }
  
  return phone;
}

/**
 * Get text preview from lastMessage
 */
export function getMessagePreview(chat) {
  const msg = chat.lastMessage?.message;
  if (!msg || typeof msg !== 'object') return '';

  if (msg.conversation) return msg.conversation;
  if (msg.extendedTextMessage?.text) return msg.extendedTextMessage.text;
  if (msg.imageMessage) return msg.imageMessage.caption || '📷 Foto';
  if (msg.videoMessage) return msg.videoMessage.caption || '🎥 Vídeo';
  if (msg.audioMessage) return '🎤 Mensagem de voz';
  if (msg.documentMessage) return `📄 ${msg.documentMessage.fileName || 'Documento'}`;
  if (msg.stickerMessage) return '🏷️ Figurinha';
  if (msg.reactionMessage) return `Reagiu: ${msg.reactionMessage.text || '👍'}`;
  if (msg.contactMessage) return '👤 Contato';
  if (msg.locationMessage) return '📍 Localização';
  if (msg.pollCreationMessage) return '📊 Enquete';

  return '';
}

/**
 * Format timestamp for chat list
 */
export function formatChatTime(timestamp) {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } else if (days === 1) {
    return 'Ontem';
  } else if (days < 7) {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

/**
 * Check if two chats represent the same contact
 * Uses _phoneNumber from backend normalization
 */
export function isSameChat(chat1, chat2) {
  if (!chat1 || !chat2) return false;
  
  // Direct JID match
  if (chat1.remoteJid === chat2.remoteJid) return true;
  
  // Phone number match (from backend)
  if (chat1._phoneNumber && chat2._phoneNumber) {
    return chat1._phoneNumber === chat2._phoneNumber;
  }
  
  // Alt JID match
  if (chat1._altJid && (chat1._altJid === chat2.remoteJid || chat1._altJid === chat2._altJid)) {
    return true;
  }
  if (chat2._altJid && chat2._altJid === chat1.remoteJid) {
    return true;
  }
  
  return false;
}

/**
 * Find chat index in array by remoteJid or phone
 */
export function findChatIndex(chats, remoteJid, altJid = null) {
  const targetPhone = remoteJid?.split('@')[0];
  
  return chats.findIndex(chat => {
    // Direct match
    if (chat.remoteJid === remoteJid) return true;
    if (altJid && chat.remoteJid === altJid) return true;
    
    // Alt JID match
    if (chat._altJid === remoteJid) return true;
    if (altJid && chat._altJid === altJid) return true;
    
    // Phone match
    if (targetPhone && chat._phoneNumber === targetPhone) return true;
    
    return false;
  });
}

/**
 * Get JID for sending messages (prefer @s.whatsapp.net)
 */
export function getMessageJid(chat) {
  if (chat.remoteJid?.includes('@s.whatsapp.net')) {
    return chat.remoteJid;
  }
  
  if (chat._altJid?.includes('@s.whatsapp.net')) {
    return chat._altJid;
  }
  
  if (chat._phoneNumber && /^\d+$/.test(chat._phoneNumber)) {
    return `${chat._phoneNumber}@s.whatsapp.net`;
  }
  
  return chat.remoteJid;
}

export default {
  isGroup,
  getChatDisplayName,
  formatPhoneNumber,
  getMessagePreview,
  formatChatTime,
  isSameChat,
  findChatIndex,
  getMessageJid,
};